/**
 * Désentrelacement d'une source live Internet — miroir de
 * `backend/src/lib/live-deinterlace.ts` (listes fermées, résumé lisible).
 * Le serveur reste seul à fabriquer le filtre envoyé à CasparCG.
 */

export type DeinterlaceMode = 'AUTO' | 'FORCE' | 'OFF'
export type DeinterlaceFilter = 'BWDIF' | 'YADIF'
export type DeinterlaceRate = 'FIELD' | 'FRAME'
export type DeinterlaceParity = 'AUTO' | 'TFF' | 'BFF'

export interface LiveDeinterlace {
  mode:   DeinterlaceMode
  filter: DeinterlaceFilter
  rate:   DeinterlaceRate
  parity: DeinterlaceParity
}

export const DEFAULT_LIVE_DEINTERLACE: LiveDeinterlace = {
  mode: 'AUTO', filter: 'BWDIF', rate: 'FIELD', parity: 'AUTO',
}

const MODES: readonly DeinterlaceMode[] = ['AUTO', 'FORCE', 'OFF']
const FILTERS: readonly DeinterlaceFilter[] = ['BWDIF', 'YADIF']
const RATES: readonly DeinterlaceRate[] = ['FIELD', 'FRAME']
const PARITIES: readonly DeinterlaceParity[] = ['AUTO', 'TFF', 'BFF']

function pick<T extends string>(allowed: readonly T[], value: unknown, fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? value as T : fallback
}

export function normalizeLiveDeinterlace(value: unknown): LiveDeinterlace {
  const raw = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
  return {
    mode:   pick(MODES, raw.mode, DEFAULT_LIVE_DEINTERLACE.mode),
    filter: pick(FILTERS, raw.filter, DEFAULT_LIVE_DEINTERLACE.filter),
    rate:   pick(RATES, raw.rate, DEFAULT_LIVE_DEINTERLACE.rate),
    parity: pick(PARITIES, raw.parity, DEFAULT_LIVE_DEINTERLACE.parity),
  }
}

/** Seul le mode Forcé utilise filtre, cadence et ordre des trames. */
export function sameLiveDeinterlace(a: unknown, b: unknown): boolean {
  const left = normalizeLiveDeinterlace(a)
  const right = normalizeLiveDeinterlace(b)
  if (left.mode !== 'FORCE' || right.mode !== 'FORCE') return left.mode === right.mode
  return left.mode === right.mode && left.filter === right.filter && left.rate === right.rate && left.parity === right.parity
}

export function describeLiveDeinterlace(value: unknown): string {
  const settings = normalizeLiveDeinterlace(value)
  if (settings.mode === 'OFF') return 'Désactivé'
  if (settings.mode !== 'FORCE') return 'Auto'
  const parity = settings.parity === 'TFF' ? ' · trame haute' : settings.parity === 'BFF' ? ' · trame basse' : ''
  return `Forcé · ${settings.filter.toLowerCase()} · ${settings.rate === 'FRAME' ? '25' : '50'} i/s${parity}`
}

export type DeinterlaceStep = 'UNCHANGED' | 'TRY_ON_CH2' | 'READY_TO_SAVE'

/**
 * Où en est le réglage en cours d'édition :
 * - identique à l'antenne → rien à faire ;
 * - différent de ce qui tourne sur le canal 2 → à essayer d'abord ;
 * - identique à ce qui tourne sur le canal 2 → enregistrable pour l'antenne.
 * Une source qui n'est pas sur le canal 2 ne peut donc jamais être enregistrée.
 */
export function deinterlaceStep(draft: unknown, saved: unknown, onCh2: unknown | undefined): DeinterlaceStep {
  if (sameLiveDeinterlace(draft, saved)) return 'UNCHANGED'
  if (onCh2 === undefined || !sameLiveDeinterlace(draft, onCh2)) return 'TRY_ON_CH2'
  return 'READY_TO_SAVE'
}
